import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, Check } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";

export default function Billing() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/business/billing").then(({ data }) => setData(data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => {
    load();
  }, []);

  const choose = async (plan) => {
    setBusy(plan.id);
    try {
      const { data } = await api.post("/payments/checkout", { plan_id: plan.id, origin_url: window.location.origin });
      if (data.activated) {
        toast.success(data.waived ? "Plan activated — fees waived by admin" : "Plan activated");
        load();
      } else if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  if (!data) return <p className="text-neutral-400">Loading…</p>;
  const { business, plan, plans, stripe_configured } = data;

  return (
    <div data-testid="billing-page">
      <h1 className="text-3xl font-black">Billing</h1>
      <div className="mt-6 bg-white border border-[#E5E5E5] rounded-2xl p-6 flex flex-wrap items-center gap-6" data-testid="current-plan-card">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-400">Current plan</div>
          <div className="text-2xl font-black mt-1" data-testid="current-plan-name">{plan?.name || "None"}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-400">Status</div>
          <div className="mt-1 flex items-center gap-2">
            <span className="capitalize font-medium" data-testid="subscription-status">{business.subscription_status}</span>
            {business.fees_waived && (
              <span className="flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1" data-testid="fees-waived-badge">
                <BadgeCheck className="h-3.5 w-3.5" /> Fees waived
              </span>
            )}
          </div>
        </div>
        {!stripe_configured && (
          <p className="text-sm text-amber-700" data-testid="stripe-not-configured">
            Stripe isn't configured yet — the platform admin can add keys in Settings.
          </p>
        )}
      </div>

      <h2 className="mt-12 text-base md:text-lg font-bold">Available plans</h2>
      <div className="mt-4 grid md:grid-cols-3 gap-4">
        {plans.map((p) => (
          <div key={p.id} className={`bg-white border rounded-2xl p-6 flex flex-col ${business.plan_id === p.id ? "border-neutral-900" : "border-[#E5E5E5]"}`} data-testid={`billing-plan-${p.id}`}>
            <h3 className="font-bold">{p.name}</h3>
            <div className="mt-3">
              <span className="text-3xl font-black font-heading">£{p.price_monthly}</span>
              <span className="text-sm text-neutral-500">/mo</span>
            </div>
            <ul className="mt-4 space-y-2 text-sm text-neutral-600 flex-1">
              <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-600 shrink-0" /> {p.features?.max_forms >= 999 ? "Unlimited" : p.features?.max_forms} form(s)</li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-600 shrink-0" /> {(p.features?.max_submissions_month || 0).toLocaleString()} submissions/mo</li>
              {p.features?.sms && <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-600 shrink-0" /> SMS alerts</li>}
              {p.features?.custom_smtp && <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-600 shrink-0" /> Custom SMTP/Twilio</li>}
            </ul>
            <Button
              onClick={() => choose(p)}
              disabled={busy === p.id || business.plan_id === p.id}
              variant={business.plan_id === p.id ? "outline" : "default"}
              className="mt-6 rounded-full"
              data-testid={`choose-plan-${p.id}`}
            >
              {business.plan_id === p.id ? "Current plan" : busy === p.id ? "Working…" : business.fees_waived ? "Activate (free)" : p.price_monthly === 0 ? "Switch to Free" : "Subscribe"}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
